import { atom } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import { memo, useMemo, useState } from 'react'

import { trackingWalletsByAddressAtom } from '@root/features/wallet-tracker/atoms/reatom-tracking-wallets'

import { laneMembers, laneTrades } from '../../model/tape-lanes'
import {
  count,
  quantity,
  shortAddress,
  since,
  usd,
} from '../portfolio-screen/money'

import { DevTokensTable, HoldersTable, TopTradersTable } from './trade-makers'
import { MyPositionTable } from './trade-my-position'

import type { HoldersResource } from './pair-makers'
import type { Network } from '@root/entities/network'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { Trade } from '@root/entities/trade'

type Tab = 'trades' | 'holders' | 'traders' | 'dev' | 'mine'

const TABS: ReadonlyArray<{ tab: Tab; label: string }> = [
  { tab: 'trades', label: 'Trades' },
  { tab: 'holders', label: 'Holders' },
  { tab: 'traders', label: 'Top traders' },
  { tab: 'dev', label: 'Dev tokens' },
  { tab: 'mine', label: 'My position' },
]

/**
 * The four questions a token page gets asked, behind four tabs.
 *
 * The tape is first because it is the one read rather than searched. The other
 * three are lazy — each fetches the first time it is opened and not before,
 * which is the difference between one request on arrival and four.
 *
 * Positions and Orders are not here. Both endpoints are dead on our backend,
 * and a tab that opens onto an error is worse than a tab that is absent.
 */
export const PairTables: React.FC<{
  pair: PairModel | undefined
  pairAddress: PairAddress | undefined
  network: Network.Network
  trades: ReadonlyArray<Trade.Trade>
  holders: HoldersResource | undefined
}> = memo(({ pair, pairAddress, network, trades, holders }) => {
  const [tab, setTab] = useState<Tab>('trades')
  const [onlyTracked, setOnlyTracked] = useState(false)
  const [holdersCount] = useAtom(pair?.holdersCount ?? emptyCountAtom)
  const [creatorStats] = useAtom(pair?.creatorStats ?? emptyCreatorAtom)

  /* Everything this dev launched except the token being looked at. */
  const devTokenCount = (creatorStats?.topTokens ?? []).filter(
    (token) => String(token.address) !== String(pair?.quote.address),
  ).length

  return (
    <section className="pn tape">
      <header>
        {TABS.map((each) => (
          <button
            key={each.tab}
            className={each.tab === tab ? 'ln on' : 'ln'}
            type="button"
            onClick={() => setTab(each.tab)}
          >
            {each.label}
            {each.tab === 'holders' && Number(holdersCount) > 0 ? (
              <em className="mono">{count(Number(holdersCount))}</em>
            ) : undefined}
            {each.tab === 'dev' && devTokenCount > 0 ? (
              <em className="mono">{devTokenCount}</em>
            ) : undefined}
          </button>
        ))}
        <div className="right">
          {tab === 'trades' ? (
            <>
              {/*
                The same lane the narrow tape offers, because somebody who has
                folded that one away still wants it here. It belongs on this
                row — a row of its own was one I invented and the design does
                not have.
              */}
              <div className="seg">
                <button
                  className={onlyTracked ? undefined : 'on'}
                  type="button"
                  onClick={() => setOnlyTracked(false)}
                >
                  All
                </button>
                <button
                  className={onlyTracked ? 'on' : undefined}
                  type="button"
                  onClick={() => setOnlyTracked(true)}
                >
                  <Star />
                  Tracked
                </button>
              </div>
              <span className="tag live">live</span>
            </>
          ) : undefined}
        </div>
      </header>

      {tab === 'trades' ? (
        <PairTrades trades={trades} onlyTracked={onlyTracked} />
      ) : undefined}
      {tab === 'holders' && pair && holders ? (
        <HoldersTable pair={pair} holders={holders} />
      ) : undefined}
      {tab === 'traders' && pair && pairAddress ? (
        <TopTradersTable
          pair={pair}
          pairAddress={pairAddress}
          network={network}
        />
      ) : undefined}
      {tab === 'dev' && pair ? <DevTokensTable pair={pair} /> : undefined}
      {tab === 'mine' && pair ? (
        <MyPositionTable pair={pair} network={network} />
      ) : undefined}
    </section>
  )
})

/** Stand-ins while the pair is still arriving, so the hook count is stable. */
const emptyCountAtom = atom(0, 'emptyCountAtom')
const emptyCreatorAtom = atom(
  undefined as { topTokens?: ReadonlyArray<{ address: unknown }> } | undefined,
  'emptyCreatorAtom',
)

/**
 * What is happening in this token, now.
 *
 * The tape is the one table on a token page that is read rather than searched,
 * so it is the one that is here: newest at the top, the side in colour, and
 * the market cap each trade went off at — which is what tells you whether the
 * last five minutes were somebody arriving or somebody leaving.
 */
const PairTrades: React.FC<{
  trades: ReadonlyArray<Trade.Trade>
  onlyTracked: boolean
}> = memo(({ trades, onlyTracked }) => {
  const [tracking] = useAtom(trackingWalletsByAddressAtom)

  const members = useMemo(
    () => laneMembers({ tracked: Object.keys(tracking) }),
    [tracking],
  )

  const rows = useMemo(() => {
    const withMaker = trades.map((trade) => ({
      trade,
      maker: String(trade.maker.address),
    }))
    return laneTrades(withMaker, onlyTracked ? 'tracked' : 'all', members)
  }, [trades, onlyTracked, members])

  return (
    <div className="tapescroll">
      {rows.length === 0 ? (
        <p className="soon">
          {onlyTracked
            ? 'None of the wallets you follow has traded this.'
            : 'Waiting for the first trade…'}
        </p>
      ) : (
        <table className="tb2">
          <thead>
            <tr>
              <th>Age</th>
              <th>Side</th>
              <th>Market cap</th>
              <th>Amount</th>
              <th>Total</th>
              <th>Maker</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ trade, maker }) => (
              <tr
                key={String(trade.id)}
                className={
                  members.tracked.has(maker.toLowerCase()) ? 'lit' : undefined
                }
              >
                <td>{since(trade.timestamp.getTime())}</td>
                <td className={sideClass(trade.type)}>
                  {sideWord(trade.type)}
                </td>
                <td>{usd(trade.marketCapUsd.toNumber())}</td>
                <td>{quantity(trade.currencyAmount.toNumber())}</td>
                <td style={{ color: 'var(--t1)' }}>
                  {usd(trade.totalUsd.toNumber())}
                </td>
                <td>
                  {/*
                      The best link on this page: a maker is a wallet, and we
                      have a screen that says what that wallet has been doing.
                      Somebody watching a token wants to know who is in it.
                    */}
                  {members.tracked.has(maker.toLowerCase()) ? (
                    <span className="trackedbadge">
                      <Star />
                      TRACKED
                    </span>
                  ) : undefined}
                  <a
                    className="maker"
                    href={`/portfolio-v2?wallet=${maker}`}
                    title="See what this wallet has been trading"
                  >
                    {shortAddress(maker)}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
})

/** The one mark that means "you know this wallet". */
const Star: React.FC = memo(() => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3 L14.6 8.7 L21 9.5 L16.4 13.8 L17.6 20 L12 17 L6.4 20 L7.6 13.8 L3 9.5 L9.4 8.7 Z" />
  </svg>
))

const sideWord = (type: string) => {
  if (type === 'buy') {
    return 'Buy'
  }
  return type === 'sell' ? 'Sell' : type
}

const sideClass = (type: string) => {
  if (type === 'buy') {
    return 'good'
  }
  return type === 'sell' ? 'bad' : undefined
}
